"use client";

import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { ALPHABET_SPACE_PATTERN, INDIAN_MOBILE_NO_PATTERN } from "../../../../utils/pattern";
import { APIResponseEntity } from "../../../../interface/api-responce";
import { toast } from "react-toastify";
import { toastOptObj } from "../../../../utils/constants";
import { IssueMaterialReq } from "../../../../interface/tbs-tracker/tbs-store";
import { APIIssueMaterial } from "../../../../service/api-service";
import { checkValidInputOnKeyDown, removeSpaceOnFirstCharacter } from "../../../../service/shared.service";

interface IssueMaterialDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onIssue: (skinQty: number, handleQty: number) => void;
    tbsNumber: string;
    tbsId: string;
    ReceivedQty: number;
    alreadyIssuedQty: number;
    alreadyIssuedSkinQty?: number;
    alreadyIssuedHandleQty?: number;
    /** handle locations of this TBS and how many of them already have a Fabrication Code */
    handleCount?: number;
    fabricationCodeCount?: number;
}

// quantities are kept as text so an input can be empty (= not issued)
interface IssueMaterialFormEntity {
    skinQty: string;
    handleQty: string;
    concernPersonName: string;
    contactNumber: string;
}

type IssueMaterialErrorField = "qty" | "skinQty" | "handleQty" | "concernPersonName" | "contactNumber";

const EMPTY_FORM: IssueMaterialFormEntity = {
    skinQty: "",
    handleQty: "",
    concernPersonName: "",
    contactNumber: "",
};

export default function IssueMaterialDialog({
    isOpen,
    onClose,
    onIssue,
    tbsId,
    tbsNumber,
    ReceivedQty,
    alreadyIssuedQty,
    alreadyIssuedSkinQty = 0,
    alreadyIssuedHandleQty = 0,
    handleCount = 0,
    fabricationCodeCount = 0,
}: IssueMaterialDialogProps) {
    const [issueMaterial, setIssueMaterial] = useState<IssueMaterialFormEntity>(EMPTY_FORM);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // Received Qty counts skins and handles together (12 = 8 skin + 4 handle); each has its own limit:
    // handles = handle locations of this TBS, skins = the rest
    const totalSkinQty = Math.max(ReceivedQty - handleCount, 0);
    const remainingSkinQty = Math.max(totalSkinQty - alreadyIssuedSkinQty, 0);
    const remainingHandleQty = Math.max(handleCount - alreadyIssuedHandleQty, 0);
    const pendingHandleCodes = Math.max(handleCount - fabricationCodeCount, 0);
    const skinQty = Number(issueMaterial.skinQty || 0);
    const handleQty = Number(issueMaterial.handleQty || 0);

    const getFormValueError = (field: IssueMaterialErrorField): string => {
        switch (field) {
            // both quantities are optional, but at least one of them must be entered
            case "qty":
                return skinQty + handleQty === 0 ? "Please enter Skin Issue Qty or Handle Issue Qty (at least one)" : "";

            case "skinQty":
                if (skinQty > remainingSkinQty) {
                    return `Skin quantity should not exceed ${remainingSkinQty}`;
                }
                if (skinQty > 0 && totalSkinQty === 0) {
                    return "No skin is assigned to this TBS";
                }
                return "";

            case "handleQty":
                if (handleQty > remainingHandleQty) {
                    return `Handle quantity should not exceed ${remainingHandleQty}`;
                }
                if (handleQty > 0 && handleCount === 0) {
                    return "No handle is assigned to this TBS";
                }
                // handles can be issued only when EVERY handle of the TBS has a Fabrication Code
                if (handleQty > 0 && pendingHandleCodes > 0) {
                    return `Capture the Fabrication Code for all handles first (${pendingHandleCodes} of ${handleCount} pending)`;
                }
                return "";

            case "concernPersonName":
                return !issueMaterial.concernPersonName.trim() ? "Please enter concern person name!" : "";

            case "contactNumber":
                if (!issueMaterial.contactNumber.trim()) {
                    return "Please enter mobile number";
                }
                return !INDIAN_MOBILE_NO_PATTERN.test(issueMaterial.contactNumber) ? "Please enter valid mobile number" : "";
        }
    };

    const showFormValueError = (field: IssueMaterialErrorField) => {
        const message = getFormValueError(field);

        if (!message) return null;

        return (
            <small
                className={`text-danger font-weight-500 p-1 d-block ${!isSubmitted ? "d-none" : ""
                    }`}
                data-error="step-error"
                data-field={field}
            >
                {message}
            </small>
        );
    };

    const onQtyChange = (field: "skinQty" | "handleQty", value: string) => {
        if (/^\d*$/.test(value)) {
            setIssueMaterial((prev) => ({
                ...prev,
                // no leading zeros ("05" -> "5"); an empty input stays empty
                [field]: value === "" ? "" : String(parseInt(value, 10)),
            }));
        }
    };

    const handleIssue = async () => {
        try {
            setIsLoading(true);
            const errorElement = document.querySelector(
                '[data-error="step-error"]'
            );
            if (errorElement) {
                setIsSubmitted(true);
                errorElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
                setIsLoading(false);
                return;
            } else {
                setIsSubmitted(false);
                const reqBody: IssueMaterialReq = {
                    tbsId,
                    skinQty,
                    handleQty,
                    concernPersonName: issueMaterial.concernPersonName.trim(),
                    contactNumber: issueMaterial.contactNumber,
                };
                const resp = await APIIssueMaterial(reqBody);
                if (!resp || !resp.data || resp.status !== 200) {
                    return;
                }
                const apiResp: APIResponseEntity = resp.data;
                if (apiResp.statusCode === 200 && apiResp.data && apiResp.status) {
                    onIssue(skinQty, handleQty);
                    setIssueMaterial(EMPTY_FORM);
                    setIsSubmitted(false);
                    onClose();
                } else {
                    toast.error(apiResp.message || apiResp.errorMsg, {
                        ...toastOptObj,
                    });
                    return;
                }
            }
        } catch (error) {
            console.log({ error });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            setIssueMaterial(EMPTY_FORM);
        }
        setIsSubmitted(false);
    }, [isOpen, tbsId]);
    return (
        <Dialog
            visible={isOpen}
            onHide={onClose}
            closable={false}
            draggable={false}
            resizable={false}
            showHeader={false}
            style={{ width: "470px" }}
            contentStyle={{ padding: "1rem" }}
        >
            <div className="px-2">
                <h4 className="card-title fw-bold text-center mb-3">
                    Issue Material
                </h4>

                <div className="mb-3">
                    <div className="mb-2">
                        <strong>TBS Number:</strong> {tbsNumber}
                    </div>

                    <div className="mb-2">
                        <strong>Received Qty:</strong> {ReceivedQty}
                    </div>

                    <div>
                        <strong>Already Issued:</strong> {alreadyIssuedQty}
                        {alreadyIssuedQty > 0 && (
                            <small className="text-muted ms-2">
                                (Skin {alreadyIssuedSkinQty}, Handle {alreadyIssuedHandleQty})
                            </small>
                        )}
                    </div>
                </div>

                <div className="row">
                    <div className="col-6">
                        <div className="form-group mb-2">
                            <label>
                                SKIN ISSUE QTY
                            </label>

                            <InputText
                                className="form-control form-control-lg"
                                maxLength={6}
                                value={issueMaterial.skinQty}
                                disabled={totalSkinQty === 0}
                                onChange={(e) => onQtyChange("skinQty", e.target.value)}
                            />

                            {showFormValueError("skinQty")}

                            <small className="text-muted mt-1 d-block">
                                {totalSkinQty === 0
                                    ? "No skin in this TBS"
                                    : `Remaining skin ${remainingSkinQty} of ${totalSkinQty}`}
                            </small>
                        </div>
                    </div>

                    <div className="col-6">
                        <div className="form-group mb-2">
                            <label>
                                HANDLE ISSUE QTY
                            </label>

                            <InputText
                                className="form-control form-control-lg"
                                maxLength={6}
                                value={issueMaterial.handleQty}
                                disabled={handleCount === 0}
                                onChange={(e) => onQtyChange("handleQty", e.target.value)}
                            />

                            {showFormValueError("handleQty")}

                            <small className="text-muted mt-1 d-block">
                                {handleCount === 0
                                    ? "No handle in this TBS"
                                    : `Remaining handles ${remainingHandleQty} of ${handleCount}. Fabrication Code captured for ${fabricationCodeCount} of ${handleCount} handles`}
                            </small>
                        </div>
                    </div>
                </div>

                <div className="mb-3">
                    {showFormValueError("qty")}

                    <small className="text-muted d-block">
                        Enter Skin Issue Qty, Handle Issue Qty or both. Skin and handle are counted separately.
                    </small>
                </div>

                <div className="form-group mb-3">
                    <label>
                        CONCERN PERSON NAME
                        <sup className="mandatory">*</sup>
                    </label>

                    <InputText
                        className="form-control form-control-lg"
                        value={issueMaterial.concernPersonName}
                        onChange={(e) =>
                            setIssueMaterial((prev) => ({
                                ...prev,
                                concernPersonName: e.target.value,
                            }))
                        }
                        maxLength={30}
                        onKeyDown={(event: any) => {
                            checkValidInputOnKeyDown(event, ALPHABET_SPACE_PATTERN);
                            removeSpaceOnFirstCharacter(event);
                        }}
                    />

                    {showFormValueError("concernPersonName")}
                </div>

                <div className="form-group mb-4">
                    <label>
                        CONTACT NUMBER
                        <sup className="mandatory">*</sup>
                    </label>

                    <InputText
                        className="form-control form-control-lg"
                        maxLength={10}
                        value={issueMaterial.contactNumber}
                        onChange={(e) => {
                            const value = e.target.value;

                            if (/^\d*$/.test(value)) {
                                setIssueMaterial((prev) => ({
                                    ...prev,
                                    contactNumber: value,
                                }));
                            }
                        }}
                    />

                    {showFormValueError("contactNumber")}
                </div>

                <div className="d-flex justify-content-end">
                    <button
                        type="button"
                        className="btn btn-primary btn-md font-weight-medium me-2"
                        onClick={onClose}
                    >
                        Close
                    </button>

                    <button
                        type="button"
                        className="btn btn-success btn-md font-weight-medium"
                        onClick={handleIssue}
                        disabled={isLoading}
                    >
                        Issue
                    </button>
                </div>

            </div>
        </Dialog>
    );
}
